// AIBreaker.js
import React, { useState, useEffect, useRef, useCallback } from 'react';

// --- Constants ---
const GAME_WIDTH = 600;
const GAME_HEIGHT = 400;
const BALL_RADIUS = 5;
const NUM_BALLS = 5;
const BALL_SPEED = 2.5; // Pixels per frame - Higher speeds increase risk of tunnelling
const BRICK_ROWS = 5;
const BRICK_COLS = 10;
const TEXT_TO_REVEAL = 'Gale Force AI';
const EPSILON = 0.1; // Small value to push ball slightly out of collision

// --- Helper Functions ---
const getRandomInitialAngle = () => {
    const angleDegrees = -30 - Math.random() * 120; // Mostly upwards
    return angleDegrees * (Math.PI / 180);
};

// --- Component ---
function AIBreaker() {
    const [balls, setBalls] = useState([]);
    const [bricks, setBricks] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const gameAreaRef = useRef(null);
    const textRef = useRef(null);
    const animationFrameId = useRef(null);

    // --- Initialization ---
    useEffect(() => {
        if (!textRef.current || isInitialized) return;

        const textRect = textRef.current.getBoundingClientRect();
        const gameAreaRect = gameAreaRef.current.getBoundingClientRect();
        const textTop = textRect.top - gameAreaRect.top;
        const textLeft = textRect.left - gameAreaRect.left;
        const textWidth = textRect.width;
        const textHeight = textRect.height;

        // Initialize Bricks
        const initialBricks = [];
        const brickWidth = textWidth / BRICK_COLS;
        const brickHeight = textHeight / BRICK_ROWS;
        for (let r = 0; r < BRICK_ROWS; r++) {
            for (let c = 0; c < BRICK_COLS; c++) {
                initialBricks.push({
                    x: textLeft + c * brickWidth,
                    y: textTop + r * brickHeight,
                    width: brickWidth,
                    height: brickHeight,
                    isVisible: true,
                    id: `brick-${r}-${c}`,
                });
            }
        }
        setBricks(initialBricks);

        // Initialize Balls
        const initialBalls = [];
        const startX = GAME_WIDTH / 2;
        const startY = GAME_HEIGHT - 30;
        for (let i = 0; i < NUM_BALLS; i++) {
            const angle = getRandomInitialAngle();
            initialBalls.push({
                x: startX,
                y: startY,
                dx: Math.cos(angle) * BALL_SPEED,
                dy: Math.sin(angle) * BALL_SPEED,
                radius: BALL_RADIUS,
                id: `ball-${i}`,
            });
        }
        setBalls(initialBalls);
        setIsInitialized(true);
        setGameOver(false);

    }, [isInitialized]);

    // --- Game Loop ---
    const gameLoop = useCallback(() => {
        if (gameOver || !isInitialized) return;

        let hitBrickIdsInFrame = new Set();

        const nextBalls = balls.map(ball => {
            let { x, y, dx, dy } = ball; // Current state
            let newX = x + dx; // Potential next position
            let newY = y + dy;

            // --- Wall Collision ---
            if (newX + BALL_RADIUS > GAME_WIDTH) {
                dx = -Math.abs(dx); // Ensure direction is negative
                newX = GAME_WIDTH - BALL_RADIUS;
            } else if (newX - BALL_RADIUS < 0) {
                dx = Math.abs(dx); // Ensure direction is positive
                newX = BALL_RADIUS;
            }
            if (newY + BALL_RADIUS > GAME_HEIGHT) {
                dy = -Math.abs(dy); // Ensure direction is negative
                newY = GAME_HEIGHT - BALL_RADIUS;
            } else if (newY - BALL_RADIUS < 0) {
                dy = Math.abs(dy); // Ensure direction is positive
                newY = BALL_RADIUS;
            }

            // --- Brick Collision ---
            let bouncedThisFrame = false;
            for (const brick of bricks) {
                if (bouncedThisFrame || !brick.isVisible || hitBrickIdsInFrame.has(brick.id)) {
                    continue;
                }

                // Check for collision at the *potential* next position
                const ballLeft = newX - BALL_RADIUS;
                const ballRight = newX + BALL_RADIUS;
                const ballTop = newY - BALL_RADIUS;
                const ballBottom = newY + BALL_RADIUS;

                const brickLeft = brick.x;
                const brickRight = brick.x + brick.width;
                const brickTop = brick.y;
                const brickBottom = brick.y + brick.height;

                if (
                    ballRight > brickLeft &&
                    ballLeft < brickRight &&
                    ballBottom > brickTop &&
                    ballTop < brickBottom
                ) {
                    // --- Collision Detected ---
                    hitBrickIdsInFrame.add(brick.id);

                    // Calculate overlaps to determine collision normal
                    const overlapLeft = ballRight - brickLeft;
                    const overlapRight = brickRight - ballLeft;
                    const overlapTop = ballBottom - brickTop;
                    const overlapBottom = brickBottom - ballTop;

                    // Find the minimum overlap dimension
                    const minOverlapX = Math.min(overlapLeft, overlapRight);
                    const minOverlapY = Math.min(overlapTop, overlapBottom);

                    // Determine collision axis based on minimum overlap
                    if (minOverlapX < minOverlapY) {
                        // Horizontal Collision
                        dx = -dx; // Reverse horizontal velocity
                        // Reposition horizontally
                        if (overlapLeft < overlapRight) { // Came from left
                            newX = brickLeft - BALL_RADIUS - EPSILON;
                        } else { // Came from right
                            newX = brickRight + BALL_RADIUS + EPSILON;
                        }
                        // Ensure vertical position is consistent with potential movement *before* this bounce
                        // This prevents snapping vertically when hitting a side.
                        newY = y + dy;

                    } else { // Vertical Collision (or equal overlap, prioritize vertical)
                        dy = -dy; // Reverse vertical velocity
                        // Reposition vertically
                        if (overlapTop < overlapBottom) { // Came from top
                            newY = brickTop - BALL_RADIUS - EPSILON;
                        } else { // Came from bottom
                            newY = brickBottom + BALL_RADIUS + EPSILON;
                        }
                        // Ensure horizontal position is consistent with potential movement *before* this bounce
                        newX = x + dx; // Use original dx before potential reversal on this axis
                    }

                    // Boundary checks after repositioning (important!)
                    newX = Math.max(BALL_RADIUS, Math.min(GAME_WIDTH - BALL_RADIUS, newX));
                    newY = Math.max(BALL_RADIUS, Math.min(GAME_HEIGHT - BALL_RADIUS, newY));


                    bouncedThisFrame = true; // Mark bounce for this ball
                    break; // Only one brick interaction per ball per frame update
                }
            } // End brick loop

            return { ...ball, x: newX, y: newY, dx, dy };
        }); // End balls.map

        // Update balls state
        setBalls(nextBalls);

        // Update bricks state if any were hit
        if (hitBrickIdsInFrame.size > 0) {
            setBricks(prevBricks =>
                prevBricks.map(brick =>
                    hitBrickIdsInFrame.has(brick.id)
                        ? { ...brick, isVisible: false }
                        : brick
                )
            );
        }

        // Continue the loop
        animationFrameId.current = requestAnimationFrame(gameLoop);

    }, [balls, bricks, gameOver, isInitialized]);

    // --- Effect to Check for Game Over ---
    useEffect(() => {
        if (isInitialized && !gameOver && bricks.length > 0) {
            const allBricksBroken = bricks.every(brick => !brick.isVisible);
            if (allBricksBroken) {
                console.log('All bricks broken! Game Over.');
                setGameOver(true);
                setBalls([]); // Explode balls
                 if (animationFrameId.current) {
                    cancelAnimationFrame(animationFrameId.current); // Stop loop
                }
            }
        }
    }, [bricks, isInitialized, gameOver]);

    // --- Start/Stop Game Loop ---
    useEffect(() => {
        if (isInitialized && !gameOver) {
            animationFrameId.current = requestAnimationFrame(gameLoop);
        } else {
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
        }
        return () => { // Cleanup
            if (animationFrameId.current) {
                cancelAnimationFrame(animationFrameId.current);
            }
        };
    }, [isInitialized, gameOver, gameLoop]);

    // --- Styles --- (Keeping dark mode styles from previous version)
    const gameAreaStyle = {
        width: `${GAME_WIDTH}px`,
        height: `${GAME_HEIGHT}px`,
        backgroundColor: '#212529',
        border: '1px solid #495057',
        position: 'relative',
        overflow: 'hidden',
        margin: '20px auto',
    };

    const textStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        fontFamily: 'Arial, sans-serif',
        fontSize: '48px',
        fontWeight: 'bold',
        color: '#f8f9fa',
        zIndex: 1,
        whiteSpace: 'nowrap',
        userSelect: 'none',
    };

    const brickStyle = (brick) => ({
        position: 'absolute',
        left: `${brick.x}px`,
        top: `${brick.y}px`,
        width: `${brick.width}px`,
        height: `${brick.height}px`,
        backgroundColor: '#6c757d',
        border: '1px solid #495057',
        boxSizing: 'border-box',
        zIndex: 2,
        transition: 'opacity 0.3s ease-out',
        opacity: brick.isVisible ? 1 : 0,
        pointerEvents: 'none',
    });

     const ballStyle = (ball) => ({
        position: 'absolute',
        left: `${ball.x - ball.radius}px`,
        top: `${ball.y - ball.radius}px`,
        width: `${ball.radius * 2}px`,
        height: `${ball.radius * 2}px`,
        backgroundColor: '#17a2b8',
        borderRadius: '50%',
        zIndex: 3,
        pointerEvents: 'none',
    });

    // --- Render ---
    return (
        <div ref={gameAreaRef} style={gameAreaStyle}>
            <div ref={textRef} style={textStyle}>
                {TEXT_TO_REVEAL}
            </div>
            {bricks.map(brick => (
                 <div key={brick.id} style={brickStyle(brick)}></div>
            ))}
            {balls.map(ball => (
                <div key={ball.id} style={ballStyle(ball)}></div>
            ))}
        </div>
    );
}

export default AIBreaker;